/**
 * kabuk.tsx
 *
 * NE İŞE YARAR : Oturum açıldıktan sonraki çerçeve — üst çubuk, sol menü, içerik alanı
 * KAYNAK SPEC  : 15-arayuz-standardi.md §5.1 (yapı) · §5.2 (menü role göre) · §5.3
 *                (scope seçici) · §7.1 (yetkisi olmayan hiç görmez) · §4.4 (çizgi, gölge)
 *                §13 (klavyeyle gezinme, görünür odak) · ADR-001 (kapsam dışı: bildirim)
 * BAĞLI OLDUĞU : @mantine/core · react-router-dom · src/kabuk/menu.ts
 *                src/kabuk/scope-baglami.tsx · src/oturum/baglam.tsx
 * KULLANAN     : src/uygulama.tsx — bütün korumalı yolların ortak düzeni
 * DİKKAT       : 1) KABUK YALNIZ KORUMALI YOLLARI SARAR. Kimlik ekranlarında kabuk
 *                   **yoktur** (`ekran/kimlik/duzen.tsx` notu 1): henüz kim olduğu
 *                   bilinmeyen bir kullanıcıya rolüne göre kurulmuş bir menü
 *                   gösterilemez.
 *                2) MENÜ HER ÇİZİMDE İZİNDEN YENİDEN KURULUR (`menu.ts`). Bir kez
 *                   hesaplanıp saklansaydı, yetkisi oturum sırasında değişen bir
 *                   kullanıcı (ADR-008 §3 anlık iptal) eski menüsünü görmeye devam
 *                   ederdi.
 *                3) BİLDİRİM ZİLİ **YOK** ve bu bir eksiklik değil: `15` §5.1 üst
 *                   çubukta bildirim sayıyor ama bildirim kanalları ADR-001 ile
 *                   **kapsam dışı**. Zil konsaydı hiç dolmayan bir kutuya çıkardı.
 *                   Anlık geri bildirim `15` §9'un bildirim düzeneğiyle veriliyor.
 *                4) ARAMA KUTUSU DA **YOK**. `15` §5.1 sayıyor ama arama, kişi
 *                   listesinin `q` parametresine (`M3-01`) dayanıyor ve kişi ekranı
 *                   henüz yazılmadı (19. adım). Hiçbir yere gitmeyen bir arama kutusu,
 *                   çalışmayan bir düğmedir; ekran geldiğinde üst çubuğa eklenecek.
 *                5) SCOPE GÖSTERGESİ DAR EKRANDA DA DÜŞMEZ (`15` §5.3). Düşen şey menü
 *                   düğmesidir; scope, kullanıcının hangi yurdun verisine baktığını
 *                   söyleyen tek işarettir ve gizlenemez.
 *                6) İÇERİK ALANI `Outlet`TİR. Her korumalı ekran kendi başlığını ve
 *                   aksiyonlarını kendisi çizer (`15` §5.1 *"başlık + aksiyonlar +
 *                   içerik"*); kabuk başlığı da çizseydi, iki başlık kaynağı doğar ve
 *                   biri diğerini tekrar ederdi.
 *                7) `NavLink`İN `end` ÖZELLİĞİ BİLİNÇLİ: `/` önekiyle her yolu
 *                   eşleştirir ve ana ekran hep etkin görünürdü.
 * SON GÜNCELLEME: 2026-08-31 04:10:00
 */
import {
  AppShell,
  Burger,
  Divider,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconChevronDown,
  IconDeviceLaptop,
  IconHome,
  IconId,
  IconLogout,
} from '@tabler/icons-react';
import type { ReactElement } from 'react';
import { Link, NavLink as YolBaglantisi, Outlet, useLocation } from 'react-router-dom';

import { metin } from '../metin';
import { useOturum } from '../oturum/baglam';
import { menuyuSuz } from './menu';
import { ScopeSaglayici } from './scope-baglami';
import { ScopeSecici } from './scope-secici';
import { UstCubukArama } from './arama';
import { ANA_ICERIK_KIMLIGI, IcerigeAtla } from './icerige-atla';

export function Kabuk(): ReactElement {
  return (
    <ScopeSaglayici>
      <KabukGovdesi />
    </ScopeSaglayici>
  );
}

function KabukGovdesi(): ReactElement {
  const { ben, cikisYap, izinVar } = useOturum();
  const konum = useLocation();
  const [menuAcik, { toggle: menuyuAcKapat, close: menuyuKapat }] = useDisclosure(false);

  // Başlıktaki not 2 — her çizimde yeniden kurulur.
  const gruplar = menuyuSuz(izinVar);

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 260, breakpoint: 'sm', collapsed: { mobile: !menuAcik } }}
      padding="lg"
    >
      <AppShell.Header>
        {/* 45. adım — sayfanın ilk odaklanabilir öğesi (`15` §13). */}
        <IcerigeAtla />
        <Group h="100%" px="md" justify="space-between" wrap="nowrap" gap="md">
          <Group gap="md" wrap="nowrap" style={{ minWidth: 0 }}>
            {/* Dar ekranda düşen şey menü düğmesi DEĞİL, menünün kendisidir; düğme
                onu geri getirir. Scope göstergesi hiçbir genişlikte düşmez (not 5). */}
            <Burger opened={menuAcik} onClick={menuyuAcKapat} hiddenFrom="sm" size="sm" />
            <ScopeSecici />
            {/* 42. adım — `15` §5.1'in arama kutusu. */}
            <UstCubukArama />
          </Group>

          <KullaniciMenusu
            kimlik={ben?.user.email ?? ben?.user.phone_masked ?? ''}
            cikisYap={() => void cikisYap()}
          />
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        <AppShell.Section grow component={ScrollArea}>
          <NavLink
            component={YolBaglantisi}
            to="/"
            // Başlıktaki not 7.
            end
            label={metin('menu.anaEkran')}
            leftSection={<IconHome size={18} />}
            active={konum.pathname === '/'}
            onClick={menuyuKapat}
          />

          {gruplar.map((grup) => (
            <Stack key={grup.baslik} gap={0} mt="md">
              <Text size="xs" c="notr.6" fw={600} px="sm" pb="xs">
                {metin(grup.baslik)}
              </Text>
              {grup.ogeler.map((oge) => (
                <NavLink
                  key={oge.yol}
                  component={YolBaglantisi}
                  to={oge.yol}
                  label={metin(oge.baslik)}
                  leftSection={<oge.ikon size={18} />}
                  active={konum.pathname === oge.yol}
                  onClick={menuyuKapat}
                />
              ))}
            </Stack>
          ))}
        </AppShell.Section>
      </AppShell.Navbar>

      {/*
        `component="main"` bir landmark üretir: ekran okuyucu kullanıcısı içeriğe
        doğrudan geçebilir. `id` "içeriğe atla" bağlantısının hedefidir ve
        `tabIndex={-1}` olmadan bağlantı odağı taşımaz — çoğu tarayıcı yalnız
        odaklanabilir öğeye odak verir.
      */}
      <AppShell.Main bg="notr.0" component="main" id={ANA_ICERIK_KIMLIGI} tabIndex={-1}>
        {/* Başlıktaki not 6 — başlık ve aksiyonlar ekranın kendisinde. */}
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}

/**
 * Kullanıcı menüsü — kimlik, kendi cihazları, çıkış.
 *
 * `15` §5.1 üst çubukta *"kullanıcı menüsü"* diyor. İçindekilerin ikisi de `SELF`tir ve
 * izin gerektirmez; bu yüzden `menu.ts`in izin süzgecinden geçmez, buraya yazılır.
 */
function KullaniciMenusu({
  kimlik,
  cikisYap,
}: {
  kimlik: string;
  cikisYap: () => void;
}): ReactElement {
  return (
    <Menu position="bottom-end" shadow="md" width={220}>
      <Menu.Target>
        <UnstyledButton>
          <Group gap="xs" wrap="nowrap">
            <Text size="sm" lineClamp={1} maw={200}>
              {kimlik}
            </Text>
            <IconChevronDown size={16} />
          </Group>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item component={Link} to="/oturum" leftSection={<IconId size={16} />}>
          {metin('oturum.baslik')}
        </Menu.Item>
        <Menu.Item
          component={Link}
          to="/cihazlarim"
          leftSection={<IconDeviceLaptop size={16} />}
        >
          {metin('oturum.cihazlarim')}
        </Menu.Item>
        <Divider />
        <Menu.Item color="hata" leftSection={<IconLogout size={16} />} onClick={cikisYap}>
          {metin('oturum.cikis')}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
